import { useQuery } from '@tanstack/react-query'
import { localDateOf, payPeriodsOf } from '@kronos/domain'
import type { DayLine, Instant, LocalDate, LocalMonth } from '@kronos/domain'
import { repository } from '../../data'

/**
 * A month of my hours: the two pay periods it is made of, fetched as the two
 * periods the server already reports (`myShifts`), and laid end to end. No
 * month endpoint — a month *is* two periods, and asking for them separately
 * means the totals on the calendar are the totals on each period's report,
 * not a second sum that could disagree with them.
 */
export function useMonthHours(month: LocalMonth) {
  const [first, second] = payPeriodsOf(month)
  const a = useQuery({ queryKey: ['shifts', 'mine', first.from], queryFn: () => repository.myShifts(first.from) })
  const b = useQuery({ queryKey: ['shifts', 'mine', second.from], queryFn: () => repository.myShifts(second.from) })

  const loaded = a.data && b.data ? { a: a.data, b: b.data } : null
  const days: readonly DayLine[] = loaded ? [...(loaded.a.person?.days ?? []), ...(loaded.b.person?.days ?? [])] : []
  const latest = loaded ? (loaded.a.now > loaded.b.now ? loaded.a : loaded.b) : null
  return {
    pending: a.isPending || b.isPending,
    error: a.error ?? b.error,
    periods: [first, second] as const,
    days,
    byDate: new Map<LocalDate, DayLine>(days.map((d) => [d.date, d])),
    timeZone: latest?.timeZone ?? null,
    today: latest ? localDateOf(latest.now as Instant, latest.timeZone) : null,
  }
}
